import React, { useState } from "react"

import { snakeCase, isEqual } from "lodash-es"
import { PagyNav } from "~/src/components"
import { Bee } from "~/src/components/BeeKit"
import { SortMenu } from "~/src/components/Catalog/SortMenu"
import { iClient } from "~/src/lib/appClients"
import { matchURL } from "~/src/lib/url"
import { Pagy, ProductCatalogItem } from "~/src/serializedRecords"
import { CatalogItemGrid } from "./CatalogItemGrid"
import { CatalogFilter, CatalogFilterToggle, FilterValues } from "./CatalogFilter"

export type ProductCatalogContentProps = {
  meta: Pagy
  categories: string[]
  productFeatures: string[]
  minPrice: number
  maxPrice: number
  items: ProductCatalogItem[]
}

export function ProductCatalogContent(props: ProductCatalogContentProps) {
  const {
    items: initialItems,
    meta: { count, next, previous, series = [] },
    categories,
    productFeatures,
    minPrice = 0,
    maxPrice = 1000,
  } = props

  const [items, setItems] = useState<ProductCatalogItem[]>(initialItems ?? [])

  const [isFilterOpen, setIsFilterOpen] = useState<boolean>(true)

  const sizeTransformer = ({ height, width }: { height: number; width: number }) => {
    const newWidth = width - (isFilterOpen ? CatalogFilter.width : 0)
    return { height, width: newWidth }
  }

  const defaultFilterState = {
    categories: [],
    productFeatures: [],
    minimumQuantities: [],
    priceRange: { min: minPrice, max: maxPrice },
  }
  const [selectedFilterValues, setSelectedFilterValues] = useState<FilterValues>(defaultFilterState)

  const isFilterDirty = !isEqual(selectedFilterValues, defaultFilterState)

  const {
    categories: selectedCategories,
    productFeatures: selectedProductFeatures,
    minimumQuantities: selectedMinimumQuantities,
    priceRange: selectedPriceRange,
  } = selectedFilterValues

  const numSelectedFilters =
    selectedCategories.length +
    selectedProductFeatures.length +
    selectedMinimumQuantities.length +
    (selectedPriceRange.min !== minPrice || selectedPriceRange.max !== maxPrice ? 1 : 0)

  const handleOnFilterChange = (values: FilterValues) => {
    setSelectedFilterValues(values)
  }

  const handleClearFilters = () => {
    setSelectedFilterValues(defaultFilterState)
  }

  const handleOnApplyFilter = (values: FilterValues) => {
    const {
      categories: selectedCategories,
      productFeatures: selectedProductFeatures,
      minimumQuantities: selectedMinimumQuantities,
      priceRange: selectedPriceRange,
    } = values

    window.alert(
      "Selected Categories: " +
        selectedCategories.join(", ") +
        "\nSelected Product Features: " +
        selectedProductFeatures.join(", ") +
        "\nSelected Minimum Quantities: " +
        selectedMinimumQuantities
          .map((range) => (range.label ? range.label : `[${range.min}-${range.max}]`))
          .join(", ") +
        "\nSelected Price Ranges: " +
        `[${selectedPriceRange.min}, ${selectedPriceRange.max}]`
    )
  }

  const handleLoveProduct = async (item: ProductCatalogItem) => {
    const currentLove = item.loved

    try {
      // Eagerly update "love" state in the UI
      setItems((prevItems) =>
        prevItems.map((prevItem) =>
          prevItem.productId === item.productId ? { ...prevItem, loved: !currentLove } : prevItem
        )
      )

      await iClient.put(`/product_preferences/${item.productId}/toggle/love`)
    } catch (error) {
      // Revert "love" state in the UI if server request failed
      setItems((prevItems) =>
        prevItems.map((prevItem) =>
          prevItem.productId === item.productId ? { ...prevItem, loved: currentLove } : prevItem
        )
      )

      throw error
    }
  }

  const handleHideProduct = async (item: ProductCatalogItem) => {
    const currentHidden = item.hidden

    try {
      // Eagerly update "hidden" state in the UI
      setItems((prevItems) =>
        prevItems.map((prevItem) =>
          prevItem.productId === item.productId ? { ...prevItem, hidden: !currentHidden } : prevItem
        )
      )

      await iClient.put(`/product_preferences/${item.productId}/toggle/hidden`)
    } catch (error) {
      // Revert "hidden" state in the UI if server request failed
      setItems((prevItems) =>
        prevItems.map((prevItem) =>
          prevItem.productId === item.productId ? { ...prevItem, hidden: currentHidden } : prevItem
        )
      )

      throw error
    }
  }

  return (
    <div className="flex flex-col mx-6 h-full">
      <Bee.Searchbar className="w-full mt-8" onSearch={(value: string) => alert(`Searching for: ${value}`)} />

      <Bee.TabBar className="mt-2">
        {["All Products", "Favorites", "Hidden", "Past Orders"].map((label) => {
          const key = snakeCase(label)
          const selected = matchURL((url) => (url.searchParams.get("tab") ?? "all_products") === key)

          return (
            <Bee.TabLink key={label} href={`?tab=${key}`} selected={selected}>
              {[label, selected && `(${count})`].filter(Boolean).join(" ")}
            </Bee.TabLink>
          )
        })}
      </Bee.TabBar>

      <div className="h-full">
        <div className="flex items-center justify-between w-full mt-8">
          <CatalogFilterToggle
            className="pb-2"
            isOpen={isFilterOpen}
            numSelectedFilters={numSelectedFilters}
            onClearAll={() => handleClearFilters()}
            onToggleFilter={() => setIsFilterOpen(!isFilterOpen)}
          />
          <SortMenu />
        </div>
        <div className="flex h-full w-full">
          <CatalogFilter
            disabled={!isFilterDirty}
            isOpen={isFilterOpen}
            priceRangeBounds={{ min: minPrice, max: maxPrice }}
            values={selectedFilterValues}
            options={{ categories, productFeatures }}
            onChange={handleOnFilterChange}
            onApply={handleOnApplyFilter}
          />
          <CatalogItemGrid
            sizeTransformer={sizeTransformer}
            onLoveProduct={handleLoveProduct}
            onHideProduct={handleHideProduct}
            items={items}
          />
        </div>
        <div className="flex justify-center p-2">
          <PagyNav previous={previous} next={next} series={series} />
        </div>
      </div>
    </div>
  )
}
